import { styled, alpha, type Theme } from '@mui/material/styles';
import { Box, TextField, Button, Grid, Collapse } from '@mui/material';
import { spacing } from '../../../styles/theme';

// Grid container
export const FiltersGrid = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  paddingLeft: 0,
  paddingRight: 0,
}));

const standardFieldSurface = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: alpha(theme.palette.common.white, 0.06),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
          '& fieldset': {
            borderColor: theme.palette.text.secondary,
            borderWidth: '2px',
          },
        },
        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.12),
          '& fieldset': {
            borderColor: theme.palette.success.main,
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.25)}`,
          },
        },
      }
    : {
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#ffffff',
          '& fieldset': {
            borderColor: theme.palette.text.primary,
            borderWidth: '2px',
          },
        },
        '&.Mui-focused': {
          backgroundColor: '#ffffff',
          '& fieldset': {
            borderColor: theme.palette.success.main,
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.12)}`,
          },
        },
      };

// TextField estándar
export const StandardTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: spacing.sm,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& fieldset': {
      borderColor: theme.palette.divider,
      borderWidth: '1px',
      transition: 'all 0.3s ease',
    },
    ...standardFieldSurface(theme),
  },
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.action.disabledBackground : '#ffffff',
    color: theme.palette.text.primary,
    WebkitTextFillColor: theme.palette.text.primary,
    opacity: 1,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.success.main,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    '& legend': {
      maxWidth: 'none',
      width: 'fit-content',
      minWidth: 0,
      padding: '0 8px',
      boxSizing: 'content-box',
    },
  },
}));

// Botón moderno
export const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  },
}));

// Botón de transferencia
export const TransferButton = styled(ModernButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'error.main',
  borderColor: 'error.main',
  '&:hover:not(.Mui-disabled)': {
    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
    borderColor: theme.palette.error.main,
    color: theme.palette.error.light,
  },
  '&.MuiButton-contained:not(.Mui-disabled)': {
    color: 'white !important',
    '&:hover': {
      color: 'white !important',
    },
    '&:active': {
      color: 'white !important',
    },
    '&:focus': {
      color: 'white !important',
    },
  },
  // Deshabilitado: opacidad y color vía `sx` (mismo criterio que Eliminar en catálogo de productos)
  '&.Mui-disabled': {
    transform: 'none',
    boxShadow: 'none',
  },
  '&:hover.Mui-disabled': {
    transform: 'none',
    boxShadow: 'none',
  },
}));

// Container de botones
export const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(2),
  width: '100%',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

// Container de filtros adicionales
export const MoreFiltersContainer = styled(Collapse)(({ theme }) => ({
  marginTop: spacing.lg,
  '& .MuiCollapse-wrapper': {
    paddingTop: spacing.md,
  },
}));

// Grid de filtros adicionales
export const MoreFiltersGrid = styled(Grid)(({ theme }) => ({
  gap: spacing.md,
  paddingLeft: 0,
  paddingRight: 0,
}));

// Container de items seleccionados
export const SelectedItemsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: spacing.sm,
  backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
  borderRadius: spacing.sm,
  border: `1px solid ${alpha(theme.palette.warning.main, 0.55)}`,
  marginTop: spacing.md,
}));

// Texto de items seleccionados
export const SelectedItemsText = styled('span')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
}));

// Container de botones de acción
export const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: spacing.sm,
  alignItems: 'center',
  marginTop: spacing.md,
}));

// Divider
export const FiltersDivider = styled('hr')(({ theme }) => ({
  border: 'none',
  height: '1px',
  backgroundColor: theme.palette.divider,
  margin: `${spacing.md} 0`,
}));
